import React, { type ReactNode, type JSX, useState, type FormEvent } from "react";
import { twMerge } from "tailwind-merge";
import { type FieldControlProps, SupFieldControl, SupSlider, SupIconInput, SupIconSlider } from "@repo/core-ui";

import { AmountInput } from "./components/Input";

type HintValue = string | JSX.Element;
type InputType = "input" | "slider";

export interface TransactionInputProps extends Omit<FieldControlProps, "id" | "children" | "hint"> {
  id?: string;
  value?: string;
  variant?: "input" | "slider" | "both";
  defaultInputType?: InputType;
  hint?: HintValue | ((value: string, inputType: InputType) => HintValue);
  onChange?: (value?: string) => void;
  slider?: {
    step?: number;
    min?: number;
    max?: number;
    value?: number | number[];
    formatDisplayValue?: (value: number) => ReactNode;
    onChange?: (value: number | number[]) => void;
    onAfterChange?: (value: number | number[]) => void;
  };
  input?: {
    value?: string;
    name?: string;
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    suffix?: ReactNode;
    marks?: number | number[] | false;
    hintFormatter?: (value: string) => string;
    onFocus?: (e: FormEvent<HTMLInputElement>) => void;
    onBlur?: (e: FormEvent<HTMLInputElement>) => void;
    onChange?: (value: string) => void;
  };
}

const NUMBER_REG_EXP = /^-?\d*\.?\d*$/;

function numberFormatter(value: string, prevValue?: string) {
  if (NUMBER_REG_EXP.test(value)) {
    return value;
  }

  return prevValue ?? "";
}

function parseNumber(value: string) {
  return value !== "" ? parseFloat(value) : undefined;
}

export function SupTransactionInput({
  id = "",
  value = "",
  defaultInputType = "input",
  variant = "both",
  className,
  disabled,
  slider = {},
  input = {},
  hint,
  onChange,
  ...controlProps
}: TransactionInputProps) {
  const [inputType, setInputType] = useState<InputType>(defaultInputType);

  function handleInputChange(newValue: string) {
    const prevValue = input.value ?? value;
    const formattedValue = numberFormatter(newValue, prevValue);

    if (formattedValue !== prevValue) {
      input.onChange?.(formattedValue);
      onChange?.(formattedValue);
    }
  }

  function handleSliderChange(newValue: number | number[]) {
    if (Array.isArray(newValue)) {
      return;
    }

    slider.onAfterChange?.(newValue);
    onChange?.(newValue.toString());
  }

  const controls = (
    <span className="flex">
      <button
        className={twMerge(
          "w-5 h-5 border-0.5 transition-colors delay-200",
          inputType === "input"
            ? "bg-neutral-50 text-neutral-900 border-neutral-300 hover:border-neutral-700 hover:bg-neutral-75 focus:bg-neutral-75 focus:text-neutral-900 active:text-neutral-900 active:bg-neutral-75"
            : "text-neutral-600 border-neutral-600 hover:text-neutral-700 hover:border-neutral-700"
        )}
        onClick={() => {
          setInputType("input");
        }}
      >
        <SupIconInput />
      </button>
      <button
        className={twMerge(
          "w-5 h-5 border-0.5 transition-colors delay-200",
          inputType === "slider"
            ? "bg-neutral-50 text-neutral-900 border-neutral-300 hover:border-neutral-700 hover:bg-neutral-75 focus:bg-neutral-75 focus:text-neutral-900 active:text-neutral-900 active:bg-neutral-75"
            : "text-neutral-600 border-neutral-600 hover:text-neutral-700 hover:border-neutral-700"
        )}
        onClick={() => {
          setInputType("slider");
        }}
      >
        <SupIconSlider />
      </button>
    </span>
  );

  const hintEl = typeof hint === "function" ? hint(value, inputType) : hint;
  const amountInput = <AmountInput id={id} disabled={disabled} {...input} value={input.value ?? value} onChange={handleInputChange} />;
  const amountSlider = (
    <SupSlider grabberType="value" disabled={disabled} className="w-full" {...slider} value={slider.value ?? parseNumber(value)} onAfterChange={handleSliderChange} />
  );

  if (variant === "input") {
    return (
      <SupFieldControl {...controlProps} id={id} className={twMerge("sup-transaction-input min-w-[20rem] max-w-full", className)} hint={hintEl}>
        {amountInput}
      </SupFieldControl>
    );
  }

  if (variant === "slider") {
    return (
      <SupFieldControl {...controlProps} id={id} className={twMerge("sup-transaction-input min-w-[20rem] max-w-full", className)} hint={hintEl}>
        {amountSlider}
      </SupFieldControl>
    );
  }

  return (
    <SupFieldControl {...controlProps} id={id} className={twMerge("sup-transaction-input min-w-[20rem] max-w-full", className)} hint={hintEl} icon={controls}>
      {inputType === "input" ? amountInput : amountSlider}
    </SupFieldControl>
  );
}
